import React, { useContext, useEffect, useState, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { AppContext } from '../../AppContext';
import { useWindowSize } from '../Cards/hooks/useWindowSize';
import { LiveEventCard } from '../../components/LiveEventCard';
import { SkeletonLoader } from '../Dashboard/components/SkeletonLoader/SkeletonLoader';
import { getPokerGoEvents } from './api/getPokerGoEvents';
import { getWebinars } from './api/getWebinars';
import AppLink from '../../components/AppLink';
import { ArrowLeftSmallIcon } from '../Cards/icons/ArrowLeftSmall';
import './LiveEvents.scss';

const ITEMS_PER_PAGE = {
  s: 12,
  m: 16,
  l: 20,
};

export function LiveEvents() {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(0);
  const windowSize = useWindowSize();

  const { json, user } = useContext(AppContext);
  const hasAccessToPokerGoEvents = user.meta?.pokergo_code?.show_pokergo_events;

  const getEvents = useCallback(async () => {
    setIsLoading(true);
    const eventsJSON = json['pokergo-events'];
    const promises = [getWebinars()];

    if (hasAccessToPokerGoEvents) {
      promises.push(getPokerGoEvents(eventsJSON));
    }

    try {
      const [webinarsData, eventsData] = await Promise.allSettled(promises);

      const webinars = webinarsData?.value?.length ? webinarsData.value : [];
      const events = eventsData?.value?.collection_list || [];

      const dataToShow = [...events, ...webinars];

      const items = dataToShow.map((item) => {
        const { air_date, start_date_timestamp, end_date_timestamp } = item;
        const startDate = air_date
          ? new Date(air_date).getTime()
          : start_date_timestamp * 1000;

        return {
          ...item,
          // we should show event for 90 minutes from start time
          timestamp: end_date_timestamp * 1000 || startDate + 5400000,
          sortTimestamp: startDate,
        };
      });
      const currentDate = new Date().getTime();

      const filteredEvents = items.filter((item) => {
        return item.timestamp > currentDate;
      });

      const sortedEvents = filteredEvents.sort((a, b) => {
        // webinar events should be higher priority if timestamps are euqal
        if (a.sortTimestamp === b.sortTimestamp) {
          return (a.type === 'webinars') - (b.type === 'webinars');
        }
        return a.sortTimestamp - b.sortTimestamp;
      });

      setEvents(sortedEvents);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [json, hasAccessToPokerGoEvents, setEvents, setIsLoading]);

  useEffect(() => {
    getEvents();
  }, [getEvents]);

  useEffect(() => {
    if (windowSize.width >= 3840) {
      setPageSize(ITEMS_PER_PAGE.l);
    } else if (windowSize.width >= 1920) {
      setPageSize(ITEMS_PER_PAGE.m);
    } else {
      setPageSize(ITEMS_PER_PAGE.s);
    }
  }, [windowSize]);

  const renderData = () => {
    if (!events.length) {
      return <h4 className="LiveEvents__empty-list">No Live Events Found</h4>;
    }

    const visibleEvents = events.slice(0, currentPage * pageSize);

    return (
      <InfiniteScroll
        dataLength={visibleEvents.length}
        next={() => setCurrentPage((prevCurrent) => prevCurrent + 1)}
        hasMore={visibleEvents.length < events.length}
        loader={<h4>Loading...</h4>}
        endMessage={null}
        style={{ overflow: 'visible' }}
      >
        <div className="LiveEvents__list">
          {visibleEvents?.map((item, idx) => (
            <LiveEventCard item={item} key={idx} />
          ))}
        </div>
      </InfiniteScroll>
    );
  };

  return (
    <div className="LiveEvents">
      <h1 className="LiveEvents__title">Live Events</h1>
      <div className="LiveEvents__back-box">
        <AppLink to="/dashboard">
          <span className="LiveEvents__back-btn">
            <ArrowLeftSmallIcon />
            Back
          </span>
        </AppLink>
      </div>
      <div className="LiveEvents__wrapper">
        {isLoading ? (
          <div style={{ width: '100%' }}>
            <SkeletonLoader showSlider={false} />
          </div>
        ) : (
          renderData()
        )}
      </div>
    </div>
  );
}
